import type { FC, ReactElement, ReactNode } from 'react';

import { SessionTimer } from '@pp/core/src/components/compliance/SessionTimer';
import { CurrencyConnected } from '@pp/core/src/components/currency/CurrencyConnected';
import { TrophyIcon } from '@pp/core/src/components/promotionsV2/trophyIcon/TrophyIcon';
import { Time } from '@pp/core/src/components/time';
import { useNetPositionConnected } from '@pp/core/src/hooks/useNetPositionConnected';
import { L10nText } from '@pp/core/src/services/uiTranslations/reactBindings/L10nText';
import { useConfigSlice } from '@pp/core/src/store/slices/config/selectors';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { Trophy } from '@pp/core/src/store/slices/promotionsV2/types';
import { useIsLandscape, useIsLargerThanWide, useIsPortrait } from '@pp/core/src/styles/hooks/media';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import { useCompositeBalance } from '../../../store/compositeSelectors/useCompositeBalance';
import { useGameConfigSlice } from '../../../store/slices/config/selectors';
import { useHighflyerStateSlice } from '../../../store/slices/highflyerState/selectors';

import styles from './GameWingItem.module.css';

export enum DirectionProps {
  end = 'end',
  start = 'start',
}
interface GameWingItemProps {
  action?: ReactNode;
  dataType?: string;
  direction?: DirectionProps;
  label?: ReactNode | string;
  trophy?: Trophy;
  value?: ReactNode | string;
  lastWin?: number | null;
}

export const GameWingItem: FC<GameWingItemProps> = ({
  action,
  dataType,
  direction,
  label,
  trophy,
  value,
  lastWin,
}): ReactElement => {
  const mods = useModifiers(
    styles,
    'gameWingItem',
    {
      end: direction === DirectionProps.end,
      start: direction === DirectionProps.start,
      lastWin: lastWin ? true : false,
    },
    { withBaseClass: true }
  );

  const gameId = useGameStateSlice.gameId();
  const inDesktopMode = useIsLargerThanWide();
  const isPortrait = useIsPortrait();
  const isLandScape = useIsLandscape();
  const tableBetLimits = useConfigSlice.tableBetLimits();
  const compliance = useConfigSlice.compliance();
  const tableName = useConfigSlice.tableName();
  const hasAnyEarlyBets = useHighflyerStateSlice.hasAnyEarlyBets();

  const jurisdiction = useConfigSlice.playerConfig()?.jurisdiction;

  const balance = useCompositeBalance();
  const hideBonusBalance = useConfigSlice.getShouldMergeBonusBalance();
  const displayBonusBalance = !hideBonusBalance || jurisdiction === 'X1';
  // const netPosition = useNetPositionConnected();
  const net = useNetPositionConnected();
  const showSessionTimer = useConfigSlice.isSessionTimerVisisble();

  const aamsTicket = useGameConfigSlice.getAamsTicket();
  const aamsSessionId = useGameConfigSlice.getAamsSessionId();

  const showTimerModsLandscape = useModifiers(styles, 'timer', {
    showLandscape: isLandScape && !inDesktopMode,
    hideLandscape: !(isLandScape && !inDesktopMode),
  });

  const showTimerModsPortrait = useModifiers(styles, 'timer', {
    showPortrait: isPortrait,
    hidePortrait: !isPortrait,
  });

  return (
    <div className={styles.wingItem}>
      {/* balance and total-bet */}
      {!inDesktopMode && dataType === 'balance' && (
        <div className={styles.extraInfoBalance}>
          {tableName && <L10nText tk={tableName} />}{' '}
          {tableBetLimits && <CurrencyConnected value={tableBetLimits.min} avoidTrailingZero />}
          &nbsp;-&nbsp;
          {tableBetLimits && <CurrencyConnected value={tableBetLimits.max} avoidTrailingZero hideCurrency />}
        </div>
      )}

      {!inDesktopMode && dataType === 'total-bet' && (
        <div className={`${styles.extraInfoTotalBet} ${styles.extraBetInfoTimer}`}>
          {showSessionTimer && (
            <span className={showTimerModsLandscape}>
              <SessionTimer />
            </span>
          )}{' '}
          &nbsp;
          <span>
            ID:{gameId} <Time />
          </span>
        </div>
      )}

      <div className={mods} data-testid={dataType ? `${dataType}` : 'game-wing-item'}>
        {action && inDesktopMode && <div className={styles.gameWingItem__action}>{action}</div>}
        <div className={styles.gameWingItem__group}>
          {label && (
            <div className={styles.gameWingItem__label} data-testid={dataType ? `${dataType}-label` : 'label'}>
              {hasAnyEarlyBets ? null : trophy ? <TrophyIcon trophy={trophy} /> : null}
              {label}
            </div>
          )}
          {value && (
            <div className={styles.gameWingItem__value} data-testid={dataType ? `${dataType}-value` : 'value'}>
              {value}
            </div>
          )}
        </div>
      </div>

      {isPortrait && compliance?.showSessionId && dataType === 'balance' && (
        <div className={styles.extraInfoBalance}>ID-P: {aamsTicket}</div>
      )}

      {isPortrait && compliance?.showSessionId && dataType === 'total-bet' && (
        <div className={styles.extraInfoTotalBet}>ID-S: {aamsSessionId}</div>
      )}

      {isPortrait && displayBonusBalance && balance && dataType === 'balance' && (
        <div className={styles.extraInfoBalance}>
          <L10nText tk="BONUS" />{' '}
          <span style={{ fontWeight: 600 }}>
            <CurrencyConnected value={balance.bonus} />
          </span>
        </div>
      )}

      {isPortrait && !displayBonusBalance && net !== undefined && dataType === 'balance' && (
        <div className={styles.extraInfoBalance}>
          <L10nText tk="NET_POSITION" />{' '}
          <span style={{ fontWeight: 600 }}>
            <CurrencyConnected value={net} />
          </span>
        </div>
      )}

      {showSessionTimer && dataType === 'total-bet' && (
        <div className={`${styles.extraInfoTotalBet} ${showTimerModsPortrait}`}>
          <SessionTimer />
        </div>
      )}
    </div>
  );
};
